import { ScrollArea } from "@marine/ui/scroll-area";
import { Separator } from "@marine/ui/separator";
import { SidebarPrimaryMenu } from "./sidebar-primary-menu";
import { SidebarSearch } from "./sidebar-search";
import { SidebarMenuSection } from "./sidebar-menu-section";
import { MenuConfig } from "@/config/types";
import { MENU_SIDEBAR_MAIN, MODULE_CONFIG } from "@/config/layout.config";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { filterMenuByPermission } from "@/lib/menu-filter";
import { useEmployee } from "@/modules/tenant/hooks/useTenantAuth";
import { detectModuleFromPath } from "@/lib/breadcrumb";
import type { PortalType, TenantModule } from "@/lib/breadcrumb";

export type { PortalType, TenantModule };

interface SidebarSecondaryProps {
  menuConfig?: MenuConfig;
  portalType?: PortalType;
}

export function SidebarSecondary({ 
  menuConfig, 
  portalType = 'platform',
}: SidebarSecondaryProps) {
  const { pathname } = useLocation();
  const activeModule = detectModuleFromPath(pathname);
  const permissions = useEmployee()?.permissions;
  // D15-23 — UX-only filter; breadcrumbs keep resolving through the
  // unfiltered MODULE_CONFIG.
  const dashboardMenu = useMemo(
    () => filterMenuByPermission(MODULE_CONFIG[activeModule].dashboard, permissions),
    [activeModule, permissions],
  );

  // Platform portal - just show the platform menu
  if (portalType === 'platform') {
    return (
      <ScrollArea className="grow shrink-0 h-[calc(100vh-1rem)] lg:h-[calc(100vh-4rem)] mt-0 mb-2.5">
        <SidebarSearch />
        <SidebarPrimaryMenu menuConfig={menuConfig || MENU_SIDEBAR_MAIN} />
      </ScrollArea>
    );
  }

  // Tenant portal - get module config and render dynamically
  const moduleConfig = MODULE_CONFIG[activeModule];
  // #1406 — sections honour requiredPermission too (UX only); a section
  // left with no entries is dropped rather than rendered as an empty header.
  const sections = moduleConfig.sections
    .map((section) => ({ ...section, menu: filterMenuByPermission(section.menu, permissions) }))
    .filter((section) => section.menu.length > 0);

  return (
    <ScrollArea className="grow shrink-0 h-[calc(100vh-1rem)] lg:h-[calc(100vh-4rem)] mt-0 mb-2.5">
      <SidebarSearch />
      
      {/* Module Dashboard */}
      <SidebarPrimaryMenu menuConfig={dashboardMenu} />
      
      {/* Module Sections (dynamically rendered) */}
      {sections.map((section) => (
        <div key={section.id}>
          <Separator className="my-2.5" />
          <SidebarMenuSection
            config={section.menu}
            sectionId={section.id}
            defaultExpanded={!section.defaultCollapsed}
          />
        </div>
      ))}
      
      {/* Placeholder message for modules under development */}
      {moduleConfig.placeholder && (
        <div className="px-4 py-8 text-center text-muted-foreground">
          <p className="text-sm">{moduleConfig.placeholder}</p>
        </div>
      )}
    </ScrollArea>
  );
}
